import { MISSING_VALUE } from '@/ui/kit';

/**
 * Presentation d'un message du transport d'echec.
 *
 * Fonctions pures : l'ecran ne fait que les appeler, et chacune se teste sans
 * rendu.
 */

/**
 * Libelle des traitements connus, par **nom court** de classe. Il n'y a qu'une
 * route asynchrone dans le projet (`FamilySharingInvitationEmailMessage`,
 * ADR 0025) : un traitement ajoute demande une ligne ici, et en attendant il
 * s'affiche sous son nom court — jamais masque, jamais un identifiant.
 */
const PROCESSING_LABELS: Readonly<Record<string, string | undefined>> = {
  FamilySharingInvitationEmailMessage: 'Invitation de coparent',
};

/** `App\FamilySharing\Infrastructure\Foo` -> `Foo`. */
export const shortClassName = (fqcn: string): string => {
  const segments = fqcn.split('\\');

  return segments[segments.length - 1] ?? fqcn;
};

export const processingLabel = (fqcn: string): string => {
  const short = shortClassName(fqcn);

  return PROCESSING_LABELS[short] ?? (short === '' ? MISSING_VALUE : short);
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const ABSOLUTE = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const parse = (iso: string | null): Date | null => {
  const date = iso === null ? null : new Date(iso);

  return date === null || Number.isNaN(date.getTime()) ? null : date;
};

/** Date complete, pour l'infobulle ; `null` si absente ou illisible. */
export const absoluteInstant = (iso: string | null): string | null => {
  const date = parse(iso);

  return date === null ? null : ABSOLUTE.format(date);
};

/**
 * « il y a 3 h ». Une date dans le futur (horloges decalees) se lit « à
 * l'instant » plutot qu'une duree negative ; au-dela de trente jours, la date.
 */
export const relativeInstant = (iso: string | null, now: number): string | null => {
  const date = parse(iso);

  if (date === null) {
    return null;
  }

  const elapsed = now - date.getTime();

  if (elapsed < MINUTE) {
    return 'à l’instant';
  }

  if (elapsed < HOUR) {
    return `il y a ${String(Math.floor(elapsed / MINUTE))} min`;
  }

  if (elapsed < DAY) {
    return `il y a ${String(Math.floor(elapsed / HOUR))} h`;
  }

  if (elapsed < 30 * DAY) {
    return `il y a ${String(Math.floor(elapsed / DAY))} j`;
  }

  return ABSOLUTE.format(date);
};

export const ERROR_PREVIEW_LENGTH = 90;

/** Texte replie d'une erreur ; `expandable` dit s'il y a quelque chose a deplier. */
export const previewError = (
  error: string,
  expanded: boolean,
): { readonly text: string; readonly expandable: boolean } => {
  const expandable = error.length > ERROR_PREVIEW_LENGTH;

  return { text: expandable && !expanded ? `${error.slice(0, ERROR_PREVIEW_LENGTH).trimEnd()}…` : error, expandable };
};

const plural = (count: number, one: string, many: string): string => `${String(count)} ${count > 1 ? many : one}`;

export interface ReplayTally {
  readonly replayed: number;
  readonly failed: number;
  /** Messages que la boucle n'a pas tentes parce qu'un echec l'a arretee. */
  readonly notTried: number;
}

/** « 2 rejoués, 0 échec » ; la fin dit pourquoi des messages n'ont pas ete tentes. */
export const describeTally = ({ replayed, failed, notTried }: ReplayTally): string => {
  const head = `${plural(replayed, 'rejoué', 'rejoués')}, ${plural(failed, 'échec', 'échecs')}`;

  return notTried === 0
    ? head
    : `${head}. Arrêt au premier échec : ${plural(notTried, 'message non tenté', 'messages non tentés')}.`;
};
